import { useState, useRef, useCallback, useEffect } from "react";
import { auth } from "./firebase";
import { PoseLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Barlow:wght@300;400;500;600&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --bg: #060b18; --bg2: #0a1020; --cyan: #00c8ff; --purple: #8b5cf6;
    --blue: #2563eb; --blue-h: #1d4ed8; --white: #f0f4ff;
    --muted: #4a5568; --border: rgba(255,255,255,0.07);
    --grad: linear-gradient(90deg, #00c8ff, #8b5cf6);
  }

  body { background: var(--bg); color: var(--white); font-family: 'Barlow', sans-serif; }

  .analyze-wrap { min-height: 100vh; background: var(--bg); position: relative; overflow: hidden; }

  .particles { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
  .particle {
    position: absolute; width: 2px; height: 2px;
    background: var(--cyan); border-radius: 50%; opacity: 0;
    animation: float linear infinite;
  }
  @keyframes float {
    0%   { opacity: 0; transform: translateY(100vh) scale(0); }
    10%  { opacity: 0.4; }
    90%  { opacity: 0.2; }
    100% { opacity: 0; transform: translateY(-10vh) scale(1.5); }
  }

  .topbar {
    position: sticky; top: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 28px;
    background: rgba(6,11,24,0.92); backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
  }
  .logo {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 900;
    font-size: 20px; letter-spacing: 2px;
    background: var(--grad); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .logo span { font-weight: 300; font-size: 11px; color: var(--muted); -webkit-text-fill-color: var(--muted); display: block; letter-spacing: 3px; }
  .back-btn {
    background: transparent; border: 1px solid var(--border);
    color: var(--white); padding: 8px 18px; border-radius: 6px;
    font-family: 'Barlow', sans-serif; font-size: 13px; cursor: pointer; transition: all 0.2s;
  }
  .back-btn:hover { border-color: var(--cyan); color: var(--cyan); }

  .content {
    position: relative; z-index: 1;
    max-width: 800px; margin: 0 auto; padding: 48px 24px 60px;
  }

  .step-header { text-align: center; margin-bottom: 40px; }
  .step-num {
    font-family: 'Barlow Condensed', sans-serif; font-size: 11px;
    letter-spacing: 4px; color: var(--cyan); text-transform: uppercase; margin-bottom: 8px;
  }
  .step-title {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 900;
    font-size: 36px; letter-spacing: 1px;
    background: var(--grad); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .step-sub { font-size: 14px; color: var(--muted); margin-top: 8px; }

  /* ── Profile Form ── */
  .profile-wrap {
    max-width: 560px; margin: 0 auto; padding: 48px 24px 80px;
    position: relative; z-index: 1;
  }
  .profile-hero-label {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 900;
    font-size: 11px; letter-spacing: 4px; color: var(--cyan);
    text-transform: uppercase; margin-bottom: 10px;
  }
  .profile-title {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 900;
    font-size: 38px; letter-spacing: 1px;
    background: var(--grad); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    margin-bottom: 8px;
  }
  .profile-sub { font-size: 14px; color: var(--muted); margin-bottom: 36px; line-height: 1.6; }

  .form-card {
    background: rgba(10,16,32,0.7);
    border: 1px solid var(--border);
    border-radius: 20px;
    padding: 32px 28px;
    backdrop-filter: blur(8px);
  }
  .form-section-label {
    font-size: 10px; font-weight: 600; letter-spacing: 3px;
    color: var(--muted); text-transform: uppercase;
    margin-bottom: 16px; margin-top: 24px;
  }
  .form-section-label:first-child { margin-top: 0; }

  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .form-group { display: flex; flex-direction: column; gap: 6px; }
  .form-group.full { grid-column: 1 / -1; }

  .form-label {
    font-size: 11px; font-weight: 600; letter-spacing: 1.5px;
    color: rgba(240,244,255,0.5); text-transform: uppercase;
  }
  .form-input {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px; padding: 11px 14px;
    color: var(--white); font-family: 'Barlow', sans-serif; font-size: 14px;
    transition: border-color 0.2s, background 0.2s; outline: none;
  }
  .form-input::placeholder { color: rgba(74,85,104,0.7); }
  .form-input:focus { border-color: var(--cyan); background: rgba(0,200,255,0.04); }

  .form-select {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px; padding: 11px 14px;
    color: var(--white); font-family: 'Barlow', sans-serif; font-size: 14px;
    transition: border-color 0.2s; outline: none; cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%234a5568' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
  }
  .form-select:focus { border-color: var(--cyan); }
  .form-select option { background: #0a1020; }

  .hand-toggle { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .hand-btn {
    padding: 12px; border-radius: 10px;
    border: 1px solid rgba(255,255,255,0.1);
    background: rgba(255,255,255,0.03);
    color: var(--muted); font-family: 'Barlow', sans-serif;
    font-size: 13px; font-weight: 500; cursor: pointer;
    transition: all 0.2s; letter-spacing: 0.3px;
  }
  .hand-btn:hover { border-color: rgba(0,200,255,0.3); color: var(--white); }
  .hand-btn.active {
    border-color: var(--cyan); color: var(--cyan);
    background: rgba(0,200,255,0.08);
  }

  .continue-btn {
    width: 100%; margin-top: 28px;
    padding: 14px; border-radius: 10px; border: none;
    background: linear-gradient(90deg, #2563eb, #1d4ed8);
    color: #fff; font-family: 'Barlow', sans-serif;
    font-size: 15px; font-weight: 600; cursor: pointer;
    transition: opacity 0.2s, transform 0.2s, box-shadow 0.2s;
    letter-spacing: 0.3px;
  }
  .continue-btn:hover:not(:disabled) {
    opacity: 0.92; transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(37,99,235,0.35);
  }
  .continue-btn:disabled { opacity: 0.3; cursor: not-allowed; }

  /* Drop zone */
  .drop-zone {
    border: 2px dashed rgba(0,200,255,0.3); border-radius: 16px;
    padding: 60px 40px; text-align: center; cursor: pointer;
    transition: all 0.3s; background: rgba(0,200,255,0.03);
    position: relative;
  }
  .drop-zone:hover, .drop-zone.drag-over {
    border-color: var(--cyan); background: rgba(0,200,255,0.07);
  }
  .drop-icon { font-size: 48px; margin-bottom: 16px; }
  .drop-title { font-family: 'Barlow Condensed', sans-serif; font-weight: 700; font-size: 22px; margin-bottom: 8px; }
  .drop-sub { font-size: 13px; color: var(--muted); }
  .drop-input { display: none; }

  .video-preview {
    margin-top: 20px; border-radius: 12px; overflow: hidden;
    border: 1px solid var(--border); position: relative;
  }
  .video-preview video { width: 100%; display: block; max-height: 320px; object-fit: contain; background: #000; }

  .btn-primary {
    background: var(--blue); color: var(--white);
    border: none; padding: 14px 36px; border-radius: 8px;
    font-family: 'Barlow', sans-serif; font-weight: 600; font-size: 15px;
    cursor: pointer; transition: background 0.2s; width: 100%; margin-top: 20px;
  }
  .btn-primary:hover { background: var(--blue-h); }
  .btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Processing */
  .processing-wrap { text-align: center; }
  .progress-track {
    width: 100%; height: 6px; background: rgba(255,255,255,0.06);
    border-radius: 3px; margin: 28px 0 12px; overflow: hidden;
  }
  .progress-fill {
    height: 100%; border-radius: 3px;
    background: var(--grad);
    transition: width 0.3s ease;
  }
  .progress-pct {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 800;
    font-size: 48px;
    background: var(--grad); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .progress-stage { font-size: 13px; color: var(--muted); margin-top: 6px; letter-spacing: 1px; }

  .stage-list { display: flex; flex-direction: column; gap: 10px; margin-top: 32px; }
  .stage-item {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 16px; border-radius: 10px;
    background: var(--bg2); border: 1px solid var(--border);
    font-size: 13px; transition: all 0.3s;
  }
  .stage-item.done { border-color: rgba(34,197,94,0.3); }
  .stage-item.active { border-color: rgba(0,200,255,0.4); }
  .stage-dot {
    width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
    background: var(--muted);
  }
  .stage-dot.done { background: #22c55e; }
  .stage-dot.active { background: var(--cyan); animation: pulse 1s infinite; }
  @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.4)} }

  /* Error */
  .error-box {
    background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3);
    border-radius: 10px; padding: 16px; margin-top: 20px;
    font-size: 13px; color: #fca5a5; text-align: center;
  }

  /* Summary */
  .summary-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; margin: 28px 0; }
  @media(max-width:600px){ .summary-grid{grid-template-columns:repeat(2,1fr);} }
  .summary-card {
    background: var(--bg2); border: 1px solid var(--border);
    border-radius: 12px; padding: 18px; text-align: center;
  }
  .summary-val {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 800;
    font-size: 32px; margin-bottom: 4px;
    background: var(--grad); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .summary-label { font-size: 11px; color: var(--muted); letter-spacing: 1px; text-transform: uppercase; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  .fade-up { animation: fadeUp 0.4s ease both; }
`;

// ─── Types ───────────────────────────────────────────────────────────────────
export interface LandmarkFrame {
  timestamp: number; // video time in seconds
  landmarks: { x: number; y: number; z: number; visibility?: number }[];
}

export interface AnalysisResult {
  videoFile: File;
  videoURL: string;
  landmarkFrames: LandmarkFrame[];
  frameCount: number;
  duration: number;
}

export interface PlayerInfo {
  name: string;
  age: string;
  level: string;
  handedness: "right" | "left";
  height: string;
  weight: string;
}

interface Props {
  onBack: () => void;
  onResults: (result: AnalysisResult, playerInfo: PlayerInfo) => void;
  uid?: string;
}

const STAGES = [
  "Loading MediaPipe model",
  "Extracting video frames",
  "Running pose detection",
  "Computing metrics",
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function AnalyzePage({ onBack, onResults, uid }: Props) {
  const storageKey = `shuttlecock:profile:${uid || "guest"}`;

  const savedProfile = (): PlayerInfo => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    return { name: "", age: "", level: "intermediate", handedness: "right", height: "", weight: "" };
  };

  const [step, setStep] = useState<"profile" | "upload" | "processing" | "done" | "error">(
    () => {
      const p = savedProfile();
      return p.name.trim().length > 1 ? "upload" : "profile";
    }
  );
  const [playerInfo, setPlayerInfo] = useState<PlayerInfo>(savedProfile);

  // Save profile whenever it changes
  useEffect(() => {
    if (playerInfo.name.trim().length > 1) {
      localStorage.setItem(storageKey, JSON.stringify(playerInfo));
    }
  }, [playerInfo, storageKey]);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoURL, setVideoURL] = useState<string>("");
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stageIdx, setStageIdx] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [summary, setSummary] = useState({ frames: 0, duration: 0, shots: 34 });
  const [result, setResult] = useState<AnalysisResult | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  function renderProfile() {
    const valid = playerInfo.name.trim().length > 1;
    return (
      <div className="analyze-wrap">
        <style>{css}</style>
        <div className="topbar">
          <div className="logo">SHUTTLECOCK<span>BY STATVO</span></div>
          <button className="back-btn" onClick={onBack}>← Back</button>
        </div>
        <div className="profile-wrap">
          <div className="profile-hero-label">Step 1 of 2</div>
          <div className="profile-title">Player Profile</div>
          <div className="profile-sub">Tell us about yourself so we can calibrate the analysis correctly.</div>
          <div className="form-card">
          <div className="form-section-label">Identity</div>
          <div className="form-grid">
            <div className="form-group full">
              <label className="form-label">Full Name</label>
              <input className="form-input" placeholder="Full Name" value={playerInfo.name}
                onChange={e => setPlayerInfo(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Age</label>
              <input className="form-input" placeholder="e.g. 22" type="number" value={playerInfo.age}
                onChange={e => setPlayerInfo(p => ({ ...p, age: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Playing Level</label>
              <select className="form-select" value={playerInfo.level}
                onChange={e => setPlayerInfo(p => ({ ...p, level: e.target.value }))}>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="professional">Professional</option>
              </select>
            </div>
          </div>
          <div className="form-section-label">Physical Stats</div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Height (cm)</label>
              <input className="form-input" placeholder="e.g. 175" type="number" value={playerInfo.height}
                onChange={e => setPlayerInfo(p => ({ ...p, height: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Weight (kg)</label>
              <input className="form-input" placeholder="e.g. 70" type="number" value={playerInfo.weight}
                onChange={e => setPlayerInfo(p => ({ ...p, weight: e.target.value }))} />
            </div>
          </div>
          <div className="form-section-label">Dominant Hand</div>
          <div className="hand-toggle">
            <button className={`hand-btn${playerInfo.handedness === "right" ? " active" : ""}`}
              onClick={() => setPlayerInfo(p => ({ ...p, handedness: "right" }))}>🏸 Right-handed</button>
            <button className={`hand-btn${playerInfo.handedness === "left" ? " active" : ""}`}
              onClick={() => setPlayerInfo(p => ({ ...p, handedness: "left" }))}>🏸 Left-handed</button>
          </div>
          </div>
          <button className="continue-btn" disabled={!valid} onClick={() => setStep("upload")}>
            Continue to Upload →
          </button>
        </div>
      </div>
    );
  }

  // ── File handling ──
  function handleFile(file: File) {
    if (!file.type.startsWith("video/")) {
      setErrorMsg("Please upload a video file.");
      return;
    }
    setVideoFile(file);
    setVideoURL(URL.createObjectURL(file));
    setErrorMsg("");
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  // ── Main processing ──
  const runAnalysis = useCallback(async () => {
    if (!videoFile) return;
    setStep("processing");
    setProgress(0);
    setStageIdx(0);

    try {
      // Stage 0: Load MediaPipe
      setStageIdx(0);
      setProgress(5);
      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
      );
      const poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath:
            "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
          delegate: "GPU",
        },
        runningMode: "VIDEO",
        numPoses: 1,
      });
      setProgress(20);
      setStageIdx(1);

      // Stage 1: Load video into hidden element
      const video = document.createElement("video");
      video.src = URL.createObjectURL(videoFile);
      video.muted = true;
      video.crossOrigin = "anonymous";
      await new Promise<void>((res, rej) => {
        video.onloadedmetadata = () => res();
        video.onerror = () => rej(new Error("Video load failed"));
      });

      const duration = video.duration;
      const FPS = 15; // sample at 15fps for performance
      const totalFrames = Math.floor(duration * FPS);
      const landmarkFrames: LandmarkFrame[] = [];

      setProgress(25);
      setStageIdx(2);

      // Stage 2: Step through video frame-by-frame
      for (let i = 0; i < totalFrames; i++) {
        const t = i / FPS;
        video.currentTime = t;
        await new Promise<void>((res) => {
          video.onseeked = () => res();
        });

        const det = poseLandmarker.detectForVideo(video, t * 1000);
        if (det.landmarks && det.landmarks.length > 0) {
          landmarkFrames.push({ timestamp: t, landmarks: det.landmarks[0] });
        }

        const pct = 25 + Math.round((i / totalFrames) * 60);
        setProgress(pct);
      }

      setProgress(88);
      setStageIdx(3);

      // Stage 3: Brief metric computation pause
      await new Promise((res) => setTimeout(res, 600));
      setProgress(100);

      const analysisResult: AnalysisResult = {
        videoFile,
        videoURL,
        landmarkFrames,
        frameCount: landmarkFrames.length,
        duration,
      };

      setSummary({
        frames: landmarkFrames.length,
        duration: Math.round(duration),
        shots: 34,
      });
      setResult(analysisResult);

      poseLandmarker.close();
      setStep("done");
    } catch (err: any) {
      console.error(err);
      setErrorMsg(err?.message || "Analysis failed. Check console for details.");
      setStep("error");
    }
  }, [videoFile, videoURL]);

  // ── Render ──
  if (step === "profile") return renderProfile();

  return (
    <>
      <style>{css}</style>
      <div className="analyze-wrap">
        <div className="particles">
          {Array.from({ length: 14 }).map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDuration: `${8 + Math.random() * 10}s`,
                animationDelay: `${Math.random() * 8}s`,
              }}
            />
          ))}
        </div>

        <div className="topbar">
          <div className="logo">
            SHUTTLECOCK <span>BY STATVO</span>
          </div>
          <button className="back-btn" onClick={onBack}>
            ← Back
          </button>
        </div>

        <div className="content">
          {/* ── STEP 1: Upload ── */}
          {(step === "upload" || step === "error") && (
            <div className="fade-up">
              <div className="step-header">
                <div className="step-num">Step 1 of 2</div>
                <div className="step-title">Upload Your Match Video</div>
                <div className="step-sub">
                  Side-profile recording works best for accurate pose detection
                </div>
              </div>

              <div
                className={`drop-zone ${dragOver ? "drag-over" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  className="drop-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                  }}
                />
                <div className="drop-icon">🎬</div>
                <div className="drop-title">
                  {videoFile ? videoFile.name : "Drop video here"}
                </div>
                <div className="drop-sub">
                  {videoFile
                    ? `${(videoFile.size / 1024 / 1024).toFixed(1)} MB`
                    : "MP4, MOV, AVI — click or drag"}
                </div>
              </div>

              {videoURL && (
                <div className="video-preview">
                  <video ref={videoRef} src={videoURL} controls />
                </div>
              )}

              {errorMsg && <div className="error-box">⚠ {errorMsg}</div>}

              <button
                className="btn-primary"
                disabled={!videoFile}
                onClick={runAnalysis}
              >
                ▶ Run Analysis
              </button>
            </div>
          )}

          {/* ── STEP 2: Processing ── */}
          {step === "processing" && (
            <div className="processing-wrap fade-up">
              <div className="step-header">
                <div className="step-num">Step 2 of 2</div>
                <div className="step-title">Analysing Your Video</div>
                <div className="step-sub">
                  MediaPipe is processing every frame — don't close this tab
                </div>
              </div>

              <div className="progress-pct">{progress}%</div>
              <div className="progress-track">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="progress-stage">{STAGES[stageIdx]}</div>

              <div className="stage-list">
                {STAGES.map((s, i) => (
                  <div
                    key={i}
                    className={`stage-item ${
                      i < stageIdx ? "done" : i === stageIdx ? "active" : ""
                    }`}
                  >
                    <div
                      className={`stage-dot ${
                        i < stageIdx ? "done" : i === stageIdx ? "active" : ""
                      }`}
                    />
                    {s}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── STEP 3: Done ── */}
          {step === "done" && result && (
            <div className="fade-up">
              <div className="step-header">
                <div className="step-num">Analysis Complete</div>
                <div className="step-title">Your Results Are Ready</div>
                <div className="step-sub">
                  {result.frameCount} pose frames captured
                </div>
              </div>

              <div className="summary-grid">
                <div className="summary-card">
                  <div className="summary-val">{result.frameCount}</div>
                  <div className="summary-label">Frames Analysed</div>
                </div>
                <div className="summary-card">
                  <div className="summary-val">{summary.duration}s</div>
                  <div className="summary-label">Duration</div>
                </div>
                <div className="summary-card">
                  <div className="summary-val">{summary.shots}</div>
                  <div className="summary-label">Shots Detected</div>
                </div>
                <div className="summary-card">
                  <div className="summary-val">78</div>
                  <div className="summary-label">Overall Score</div>
                </div>
              </div>

              <button className="btn-primary" onClick={() => onResults(result, playerInfo)}>
                View Full Results →
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}